import classnames from "classnames";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { ChevronLeft, ChevronRight, PanelBottomClose, PanelBottomOpen } from "lucide-react";
import {
  type CSSProperties,
  Fragment,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getDateFnsLocale } from "@/lib/date-fns-locale";
import { useLocale, useTranslation } from "@/lib/i18n";
import { warningLevelI18nKey } from "@/lib/warning-level";

import { riskPictureCellLabel, riskPicturePeriodLabel } from "./cell-label";
import { riskPictureCellColor } from "./risk-picture-cell";
import {
  CHANCE_STRIPE_TILE_PX,
  chanceBackground,
  riskPictureLevelColor,
} from "./risk-picture-colors";
import {
  RISK_PICTURE_WARNING_LEVEL,
  type RiskPictureDay,
  type RiskPictureKind,
  type RiskPictureLevel,
} from "./risk-picture-model";
import { riskPictureNow } from "./risk-picture-now";
import {
  riskPicturePhenomenonIconUrl,
  riskPicturePhenomenonName,
} from "./risk-picture-phenomena";
import {
  riskPictureDayTabKeyTarget,
  riskPictureTimelineClickAction,
  type RiskPictureSelection,
} from "./risk-picture-selection";
import {
  buildRiskPictureTimeline,
  riskPictureCountdownFits,
} from "./risk-picture-timeline";
import styles from "./risk-picture.module.css";

interface RiskPictureTimelineSidebarProps {
  day: RiskPictureDay | undefined;
  /** Every day in the forecast, with its most severe code independent of map filters. */
  strip: readonly RiskPictureTimelineStripDay[];
  dayIndex: number;
  /** Table order. Rows are still only phenomena that have a warning this day. */
  phenomena: readonly string[];
  /** All of the day's items, independent of map filters. */
  items: RiskPictureDay["items"];
  /**
   * Chart height of the fullest unfiltered day.
   * A quieter day keeps this height so the band does not shrink and the map
   * does not slide up under the next-day button. 0 disables it (mobile, fullscreen drawer).
   */
  chartMinHeight: number;
  selectedItemId?: string;
  /** Warning hovered anywhere (map, sidebar, here). Its bar is outlined. */
  highlightedItemId?: string;
  /**
   * Fullscreen: only the bar stays in flow and the chart opens over the map, so toggling it
   * does not resize the map. Outside fullscreen the chart is always shown under the bar.
   */
  drawer: boolean;
  /** Ceiling for the open drawer body, in px. Only read in drawer mode. */
  drawerMaxHeight: number;
  /** Only read in drawer mode. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Height the open drawer covers over the map, in px; 0 when closed or not a drawer. */
  onDrawerHeightChange: (height: number) => void;
  onSelectDay: (index: number) => void;
  onSelectItem: (itemId: string) => void;
  onDeselectItem: () => void;
  onHoverItem: (item: RiskPictureSelection | undefined) => void;
}

const percent = (fraction: number): string => `${fraction * 100}%`;

/** Duration of `--timeline-resize` in `risk-picture.module.css`. */
const TIMELINE_RESIZE_MS = 300;

interface TimelineCountdownProps {
  offset: number;
  width: number;
  minutes: number;
  label: string;
}

const TimelineCountdown = ({ offset, width, minutes, label }: TimelineCountdownProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [fits, setFits] = useState(false);
  useLayoutEffect(() => {
    const connector = ref.current;
    const text = connector?.firstElementChild;
    if (!connector || !text) return;
    let cancelled = false;
    const measure = () => {
      if (!cancelled)
        setFits(
          riskPictureCountdownFits(
            minutes,
            connector.getBoundingClientRect().width,
            text.getBoundingClientRect().width,
          ),
        );
    };
    const observer = new ResizeObserver(measure);
    observer.observe(connector);
    observer.observe(text);
    measure();
    void document.fonts.ready.then(measure);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [minutes, label, width]);
  // A connector without its label says nothing, so hide both together.
  // `visibility` (not unmounting) keeps the geometry the ResizeObserver measures.
  return (
    <span
      ref={ref}
      className={styles.timelineCountdown}
      aria-hidden="true"
      style={{
        left: percent(offset),
        width: percent(width),
        visibility: fits ? "visible" : "hidden",
      }}
    >
      <span className={styles.timelineCountdownLabel}>{label}</span>
    </span>
  );
};

const DATE_LABEL = "eeee d MMMM";

/** date-fns weekdays are lowercase in Dutch. */
const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

export interface RiskPictureTimelineStripDay {
  /** Local calendar date, YYYY-MM-DD. */
  date: string;
  /** Drives the bar; no bar when undefined. */
  level?: RiskPictureLevel;
  /** `chance` paints the bar with the striped kansverwachting wash, as on the map. */
  kind?: RiskPictureKind;
}

/** CSS `background` for the day bar: solid code color, or the chance wash plus stripes. */
const dayLevelBackground = (level: RiskPictureLevel, kind?: RiskPictureKind): string => {
  if (kind !== "chance") return `rgb(${riskPictureLevelColor(level).join(" ")})`;
  const { base, stripes } = chanceBackground(level);
  return `${stripes} 0 0 / ${CHANCE_STRIPE_TILE_PX}px ${CHANCE_STRIPE_TILE_PX}px, ${base}`;
};

/** Full-width band under the map: the selected day's warnings on a 24-hour axis. */
export const RiskPictureTimelineSidebar = ({
  day,
  strip,
  dayIndex,
  phenomena,
  items,
  chartMinHeight,
  selectedItemId,
  highlightedItemId,
  drawer,
  drawerMaxHeight,
  open,
  onOpenChange,
  onDrawerHeightChange,
  onSelectDay,
  onSelectItem,
  onDeselectItem,
  onHoverItem,
}: RiskPictureTimelineSidebarProps) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const [now, setNow] = useState(() => riskPictureNow());
  const bodyHidden = drawer && !open;
  useEffect(() => {
    // Nothing to tick while the drawer hides the chart; opening refreshes straight away.
    if (bodyHidden) return;
    const refresh = () => setNow(riskPictureNow());
    refresh();
    const timer = window.setInterval(refresh, 5_000);
    // Background tabs throttle timers; refresh immediately when returning to the page.
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [bodyHidden]);
  const timeline = useMemo(
    () => (day ? buildRiskPictureTimeline(items, day.date, { now, phenomena }) : undefined),
    [day, items, phenomena, now],
  );

  const stripLabels = useMemo(() => {
    const dateFnsLocale = getDateFnsLocale(locale);
    return strip.map(({ date }, index) => {
      const value = new Date(date);
      return {
        // Day 0 is the local date of `now` (see `buildRiskPicture`).
        weekday:
          index === 0
            ? t("general.today")
            : capitalize(format(value, "eeee", { locale: dateFnsLocale })),
        // e.g. "28 okt". Dutch abbreviations end in "." except "mei"; dropped so all match.
        dayOfMonth: format(value, "d MMM", { locale: dateFnsLocale }).replace(/\.$/, ""),
        full: format(value, DATE_LABEL, { locale: dateFnsLocale }),
      };
    });
  }, [strip, locale, t]);
  const bodyId = useId();
  const dayTabId = (index: number) => `${bodyId}-day-${index}`;
  const bodyRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  // Drawer only: the chart's natural height, set on the body as an explicit `height` so a day
  // switch can transition it (`height: auto` does not animate).
  const [chartHeight, setChartHeight] = useState<number>();
  // True while the body animates to a new height. Hides overflow meanwhile: growing to a
  // taller day leaves the body briefly shorter than the chart, which would flash a scrollbar
  // and reflow bar widths.
  const [resizing, setResizing] = useState(false);
  const stripRef = useRef<HTMLDivElement>(null);

  // The strip scrolls sideways on narrow screens; keep the selected day in view.
  // Sets `scrollLeft` directly: `scrollIntoView` would also scroll the page to the map.
  useLayoutEffect(() => {
    const container = stripRef.current;
    const selected = container?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!container || !selected) return;
    // Roving focus: after an arrow key the old day has `tabIndex=-1`, so focus moves to the
    // new one. Only while focus is already in the strip, so clicks elsewhere never steal it.
    // `preventScroll`: the page must not jump to the map; `scrollLeft` below handles the strip.
    if (container.contains(document.activeElement) && document.activeElement !== selected) {
      selected.focus({ preventScroll: true });
    }
    // Keep the strip's padding visible beside the day, or its outline is clipped at the edge.
    const inset = Number.parseFloat(getComputedStyle(container).paddingLeft) || 0;
    const start = selected.offsetLeft - inset;
    const end = selected.offsetLeft + selected.offsetWidth + inset;
    if (start < container.scrollLeft) container.scrollLeft = start;
    else if (end > container.scrollLeft + container.clientWidth) {
      container.scrollLeft = end - container.clientWidth;
    }
  }, [dayIndex]);

  // Live region text. Tabs already announce the day on focus, so a keyboard move inside the
  // tablist would be read twice; only changes from outside it (chevrons, map filters,
  // sidebar picks) are announced. Not on mount either: nothing changed yet.
  const [announcement, setAnnouncement] = useState("");
  const announcedOnce = useRef(false);
  const dayLabel = stripLabels[dayIndex]?.full;
  useEffect(() => {
    if (!announcedOnce.current) {
      announcedOnce.current = true;
      return;
    }
    if (!dayLabel || stripRef.current?.contains(document.activeElement)) return;
    setAnnouncement(
      `${dayLabel}: ${t("riskPicture.timeline.warningCount", { count: items.length })}`,
    );
  }, [dayLabel, items.length, t]);

  // The chart's own height, not the animating body, so a day switch sets the target once.
  useLayoutEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    let timer: number | undefined;
    let previous: number | undefined;
    const measure = () => {
      const height = chart.offsetHeight;
      if (height === previous) return;
      if (previous !== undefined) {
        setResizing(true);
        window.clearTimeout(timer);
        // A timer, not `transitionend`: reduced motion drops the transition, so that
        // event would never fire. Slightly over the transition.
        timer = window.setTimeout(() => setResizing(false), TIMELINE_RESIZE_MS + 50);
      }
      previous = height;
      setChartHeight(height);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(chart);
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, []);

  // The drawer covers the bottom of the map; report how much, so map controls can sit above it.
  // Reports the height the body animates to, not its per-frame height: the controls run their
  // own `bottom` transition, which would restart every frame and lag behind.
  useLayoutEffect(() => {
    const body = bodyRef.current;
    // Inline, the chart sits under the map instead of over it, so it covers nothing.
    if (!body || !drawer || bodyHidden || chartHeight === undefined) {
      onDrawerHeightChange(0);
      return;
    }
    // Border-top; the body's horizontal padding does not add height.
    const border = body.offsetHeight - body.clientHeight;
    onDrawerHeightChange(Math.min(chartHeight, drawerMaxHeight) + border);
  }, [drawer, bodyHidden, chartHeight, drawerMaxHeight, onDrawerHeightChange]);

  return (
    <aside
      className={classnames(
        styles.timeline,
        drawer && styles.timelineDrawer,
        bodyHidden && styles.timelineDrawerClosed,
      )}
      aria-label={t("riskPicture.timeline.title")}
    >
      <div className={styles.timelineHeading}>
        <span className={styles.timelineTitle}>{t("riskPicture.timeline.title")}</span>
        {drawer && (
          <button
            type="button"
            className={styles.timelineDrawerToggle}
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => onOpenChange(!open)}
          >
            {t(open ? "riskPicture.timeline.close" : "riskPicture.timeline.open")}
            {open ? <PanelBottomClose size={20} /> : <PanelBottomOpen size={20} />}
          </button>
        )}
      </div>
      <div className={styles.timelineDayNav}>
        <button
          type="button"
          className={styles.timelineDayStep}
          aria-label={t("riskPicture.timeline.previousDay")}
          // `aria-disabled`, not `disabled`: a disabled button drops focus to <body> the moment
          // the last press reaches day 0, so the next Tab restarts at the top of the page.
          aria-disabled={dayIndex <= 0}
          onClick={() => {
            if (dayIndex > 0) onSelectDay(dayIndex - 1);
          }}
        >
          <ChevronLeft size={16} />
        </button>
        {/* WAI-ARIA tabs: one Tab stop (the selected day), ←/→/Home/End between days, and
            selection follows focus. The days control the chart body (the tabpanel). */}
        <div
          ref={stripRef}
          className={styles.timelineDayStrip}
          role="tablist"
          aria-label={t("riskPicture.timeline.days")}
          onKeyDown={(event) => {
            const next = riskPictureDayTabKeyTarget(event.key, dayIndex, strip.length);
            if (next === undefined) return;
            event.preventDefault();
            if (next !== dayIndex) onSelectDay(next);
          }}
        >
          {strip.map(({ date, level, kind }, index) => {
            const labels = stripLabels[index];
            const levelName = level
              ? `${t("common.code")} ${t(warningLevelI18nKey[RISK_PICTURE_WARNING_LEVEL[level]])}`
              : undefined;
            const selected = index === dayIndex;
            return (
              <button
                key={date}
                id={dayTabId(index)}
                type="button"
                role="tab"
                className={styles.timelineDayButton}
                aria-selected={selected}
                aria-controls={bodyId}
                tabIndex={selected ? 0 : -1}
                // Starts with the visible text ("Vandaag 28 okt"), so voice control can target
                // it (WCAG 2.5.3); the full date stays in the tooltip.
                aria-label={[`${labels.weekday} ${labels.dayOfMonth}`, levelName]
                  .filter(Boolean)
                  .join(", ")}
                title={labels.full}
                data-level={level}
                data-kind={level ? kind : undefined}
                style={
                  level
                    ? ({
                        "--day-level-bg": dayLevelBackground(level, kind),
                      } as CSSProperties)
                    : undefined
                }
                onClick={() => onSelectDay(index)}
              >
                <span className={styles.timelineDayWeekday} data-text={labels.weekday}>
                  {labels.weekday}
                </span>
                <span className={styles.timelineDayNumber}>{labels.dayOfMonth}</span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className={styles.timelineDayStep}
          aria-label={t("riskPicture.timeline.nextDay")}
          aria-disabled={dayIndex >= strip.length - 1}
          onClick={() => {
            if (dayIndex < strip.length - 1) onSelectDay(dayIndex + 1);
          }}
        >
          <ChevronRight size={16} />
        </button>
      </div>
      {/* Visually hidden. A day switch from the chevrons (or a filter change) otherwise changes
          the chart silently. Outside the body, so the closed drawer's `aria-hidden` does not
          mute it. Fed from `announcement`, not rendered directly: see the effect above. */}
      <span className={styles.visuallyHidden} aria-live="polite" aria-atomic="true">
        {announcement}
      </span>
      {/* `visibility: hidden` on the closed drawer also drops its bars from the tab order. */}
      <div
        ref={bodyRef}
        id={bodyId}
        role="tabpanel"
        aria-labelledby={dayTabId(dayIndex)}
        className={styles.sidebarBody}
        style={
          drawer
            ? {
                height: chartHeight,
                maxHeight: drawerMaxHeight,
                overflowY: resizing ? "hidden" : undefined,
              }
            : undefined
        }
        aria-hidden={bodyHidden || undefined}
      >
        <div
          ref={chartRef}
          className={styles.timelineChart}
          style={chartMinHeight > 0 ? { minHeight: chartMinHeight } : undefined}
        >
          {!timeline || timeline.rows.length === 0 ? (
            <p className={styles.sidebarEmpty}>{t("riskPicture.sidebar.noWarnings")}</p>
          ) : (
            <>
              <div className={styles.timelineAxis} aria-hidden="true">
                <span />
                <div className={styles.timelineTrack}>
                  <div className={styles.timelineScale}>
                    {timeline.ticks.map((tick) => (
                      <span
                        key={tick.hour}
                        className={styles.timelineTick}
                        style={{ left: percent(tick.offset) }}
                      >
                        {`${String(tick.hour).padStart(2, "0")}:00`}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className={styles.timelineRows}>
                {timeline.nowOffset !== undefined && (
                  <div className={styles.timelineNowOverlay} aria-hidden="true">
                    <span />
                    <div className={styles.timelineTrack}>
                      <div className={styles.timelineScale}>
                        <span
                          className={styles.timelineNow}
                          style={{ left: percent(timeline.nowOffset) }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {timeline.rows.map((row) => {
                  const phenomenonName = riskPicturePhenomenonName(row.phenomenon, t);
                  return (
                    <section key={row.phenomenon} className={styles.timelineRow}>
                      <span className={styles.timelineRowLabel} aria-label={phenomenonName}>
                        <span
                          className={styles.filterPhenomenonIcon}
                          style={{
                            maskImage: `url("${riskPicturePhenomenonIconUrl(row.phenomenon)}")`,
                            WebkitMaskImage: `url("${riskPicturePhenomenonIconUrl(row.phenomenon)}")`,
                          }}
                        />
                        {phenomenonName}
                      </span>
                      <div
                        className={classnames(styles.timelineTrack, styles.timelineLanes)}
                      >
                        <div className={styles.timelineScale}>
                          {timeline.ticks.map((tick) => (
                            <span
                              key={tick.hour}
                              className={styles.timelineGridLine}
                              style={{ left: percent(tick.offset) }}
                            />
                          ))}
                          {row.lanes.map((lane, laneIndex) => (
                            <div key={laneIndex} className={styles.timelineLane}>
                              {lane.map(({ item, offset, width, countdown }) => {
                                const label = riskPictureCellLabel(item);
                                const period = riskPicturePeriodLabel(
                                  item.onset,
                                  item.expires,
                                );
                                const countdownLabel = countdown
                                  ? `${Math.floor(countdown.minutes / 60)}h ${countdown.minutes % 60}m`
                                  : undefined;
                                const tooltip = [
                                  label,
                                  period,
                                  countdownLabel &&
                                    t("riskPicture.timeline.startsIn", {
                                      duration: countdownLabel,
                                    }),
                                ]
                                  .filter(Boolean)
                                  .join("\n");
                                return (
                                  <Fragment key={item.id}>
                                    {countdown && (
                                      <TimelineCountdown
                                        offset={countdown.offset}
                                        width={countdown.width}
                                        minutes={countdown.minutes}
                                        label={countdownLabel!}
                                      />
                                    )}
                                    <button
                                      type="button"
                                      className={classnames(
                                        styles.cell,
                                        styles.timelineBar,
                                        selectedItemId &&
                                          item.id !== selectedItemId &&
                                          item.id !== highlightedItemId &&
                                          styles.timelineBarDimmed,
                                        item.id === highlightedItemId &&
                                          styles.timelineBarHighlighted,
                                      )}
                                      aria-pressed={item.id === selectedItemId}
                                      data-kind={item.kind}
                                      data-level={item.level}
                                      style={{
                                        ...riskPictureCellColor(item),
                                        left: percent(offset),
                                        width: percent(width),
                                      }}
                                      title={tooltip}
                                      onMouseEnter={() =>
                                        onHoverItem({ dayIndex, itemId: item.id })
                                      }
                                      onMouseLeave={() => onHoverItem(undefined)}
                                      onClick={() => {
                                        onHoverItem(undefined);
                                        if (
                                          riskPictureTimelineClickAction(
                                            item.id,
                                            selectedItemId,
                                          ) === "back"
                                        ) {
                                          onDeselectItem();
                                        } else {
                                          onSelectItem(item.id);
                                        }
                                      }}
                                    >
                                      <span className={styles.cellLabel}>{label}</span>
                                    </button>
                                  </Fragment>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    </section>
                  );
                })}
              </div>
              <div className={styles.timelineNowFooter}>
                <span />
                <div className={styles.timelineTrack}>
                  <div className={styles.timelineScale}>
                    {timeline.nowOffset !== undefined && (
                      <time
                        className={styles.timelineNowLabel}
                        dateTime={now.toISOString()}
                        style={{ left: percent(timeline.nowOffset) }}
                      >
                        {formatInTimeZone(now, "Europe/Amsterdam", "HH:mm")}
                      </time>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </aside>
  );
};
